import { ArrowRightIcon, MessageCircleIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemGroup,
    ItemTitle,
    Link,
    Skeleton,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

type Category = RouterOutput['product']['community']['categories']['listCategories']['categories'][number]

type CategoryListProps = {
    categories: Category[] | null
    canManageCategories: boolean
    canDeleteCategories: boolean
    openCreateDialog: () => void
    openEditDialog: (category: Category) => void
    openCategory: (category: Category) => void
    setCategoryToDelete: (category: Category) => void
}

export const CategoryList = observer(
    ({
        categories,
        canManageCategories,
        canDeleteCategories,
        openCreateDialog,
        openEditDialog,
        openCategory,
        setCategoryToDelete,
    }: CategoryListProps) => {
        const { t } = useTranslation()

        return (
            <div className="space-y-3">
                {!categories &&
                    Array.from({ length: 4 }).map((_, index) => (
                        <Card key={`community-category-skeleton-${index.toString()}`}>
                            <CardHeader>
                                <Skeleton className="h-5 w-1/2" />
                                <Skeleton className="h-4 w-3/4" />
                            </CardHeader>
                        </Card>
                    ))}
                {categories?.length === 0 && (
                    <Card>
                        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                            <MessageCircleIcon className="text-muted-foreground size-8" />
                            <div className="font-medium">{t('product.community.home.categories.empty.title')}</div>
                            <div className="text-muted-foreground max-w-md text-sm">
                                {t('product.community.home.categories.empty.description')}
                            </div>
                            {canManageCategories && (
                                <Button onClick={openCreateDialog}>
                                    <PlusIcon data-icon="inline-start" />
                                    {t('product.community.home.categories.create')}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}
                {!!categories?.length && (
                    <ItemGroup className="gap-3">
                        {categories.map((category) => (
                            <Item
                                key={category._id.toString()}
                                variant="outline"
                                role="link"
                                tabIndex={0}
                                onClick={() => openCategory(category)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        openCategory(category)
                                    }
                                }}
                                className="bg-background hover:bg-muted group cursor-pointer items-start gap-4 rounded-lg p-5"
                                data-read-state={category.readState}
                            >
                                <ItemContent className="min-w-0 gap-1.5">
                                    <ItemTitle className="block w-full max-w-full truncate text-xl leading-7">
                                        <span
                                            className="block min-w-0 truncate data-[read-state=new]:font-normal data-[read-state=read]:font-normal data-[read-state=updated]:font-bold"
                                            data-read-state={category.readState}
                                        >
                                            {category.title}
                                        </span>
                                    </ItemTitle>
                                    <ItemDescription className="block w-full max-w-full text-base leading-6">
                                        {category.description}
                                    </ItemDescription>
                                    <div className="mt-1.5 truncate text-sm leading-5">
                                        {t('product.community.home.categories.counts', {
                                            topics: category.topicsCount,
                                            posts: category.postsCount,
                                        })}
                                    </div>
                                </ItemContent>
                                <ItemActions
                                    className="ml-auto shrink-0 items-center justify-end self-center"
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) => event.stopPropagation()}
                                >
                                    {canManageCategories && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.community.home.categories.edit')}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                openEditDialog(category)
                                            }}
                                        >
                                            <PencilIcon />
                                        </Button>
                                    )}
                                    {canDeleteCategories && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.community.home.categories.delete')}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                setCategoryToDelete(category)
                                            }}
                                        >
                                            <Trash2Icon />
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        title={t('product.community.home.categories.open')}
                                        nativeButton={false}
                                        render={
                                            <Link
                                                variant="unstyled"
                                                to="/product/community/categories/$categoryId"
                                                params={{ categoryId: category._id.toString() }}
                                                active={false}
                                            />
                                        }
                                    >
                                        <ArrowRightIcon className="text-muted-foreground size-6 shrink-0 transition-transform group-hover:translate-x-0.5" />
                                    </Button>
                                </ItemActions>
                            </Item>
                        ))}
                    </ItemGroup>
                )}
            </div>
        )
    }
)
